import { format, parseISO } from "date-fns";

/**
 * Upper-case the first letter of every whitespace-separated word in a name.
 *
 * Only the initial is touched — the rest of each word is left exactly as stored.
 * That is deliberate: lower-casing the remainder (what `capitalize` below does)
 * would rewrite names that are legitimately capitalized mid-word, turning
 * "McDonald" into "Mcdonald" and "O'Brien" into "O'brien". Directory data here
 * arrives in mixed case, so the initial is the only part safe to normalize.
 *
 * Mostly display-time, with one deliberate exception: the Excel user import
 * capitalizes names as it parses them, because those values are persisted to the
 * IAM account and profile row and so are read by consumers that do no formatting
 * of their own. Do not use it on one side of a comparison — matching elsewhere is
 * done case-insensitively — and do not apply it to an email or username.
 */
export const capitalizeName = (value?: string | null): string => {
  if (!value) return "";
  return value.replace(/\S+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1));
};

export const formatUserName = (
  firstName?: string | null,
  lastName?: string | null,
): string => {
  const clean = (part?: string | null) => {
    const value = (part ?? "").trim();
    return value.toLowerCase() === "null" ? "" : value;
  };
  return capitalizeName(
    [clean(firstName), clean(lastName)].filter(Boolean).join(" "),
  );
};

export const sanitizeUrl = (url: string): string => {
  try {
    return decodeURIComponent(url);
  } catch {
    return url;
  }
};

export const formatDisplayDate = (dateStr: string) => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const formatDisplayTime = (timeStr: string) => {
  if (!timeStr) return null;
  if (/^\d{2}:\d{2}$/.test(timeStr)) {
    const [hours, minutes] = timeStr.split(":");
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return format(date, "h:mm a");
  }
  const date = new Date(timeStr);
  return isNaN(date.getTime()) ? null : format(date, "h:mm a");
};

export const toHHmm = (timeStr?: string): string | undefined => {
  if (!timeStr) return undefined;
  if (/^\d{2}:\d{2}$/.test(timeStr)) return timeStr;
  try {
    return format(parseISO(timeStr), "HH:mm");
  } catch {
    return undefined;
  }
};

/**
 * A short plain-text preview of rich content, for somewhere that cannot render
 * it — an email body, a link preview, a push notification.
 *
 * Defensive about the input shape on purpose: article content is authored in the
 * rich text editor, so it arrives as HTML, but drafts and older records can be
 * plain text. Stripping tags is a no-op on the latter, so one path handles both.
 *
 * Truncates on a word boundary — a preview cut mid-word reads like a bug — and
 * only appends the ellipsis when something was actually removed.
 */
export const toPlainExcerpt = (
  value?: string | null,
  maxLength = 180,
): string => {
  const plain = (value ?? "")
    // Block-level tags become spaces so "<p>a</p><p>b</p>" is not "ab".
    .replace(/<(br|\/p|\/div|\/li|\/h[1-6])\s*\/?>/gi, " ")
    .replace(/<[^>]*>/g, "")
    // The handful of entities the editor emits; enough for a preview.
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, " ")
    .trim();

  if (plain.length <= maxLength) return plain;

  const clipped = plain.slice(0, maxLength);
  const lastSpace = clipped.lastIndexOf(" ");
  return `${(lastSpace > 0 ? clipped.slice(0, lastSpace) : clipped).replace(/[.,;:!?-]+$/, "")}…`;
};

export const capitalize = (value?: string | null): string => {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

/**
 * The collapsed form of body copy that the UI line-clamps.
 *
 * A hard line break inside a clamp ends the visible line early, so the
 * truncation ellipsis landed wherever the author's break happened to fall —
 * mid-width on one post, right after a two-word paragraph on the next. Folding
 * break runs into a single space makes every clamped line full width, which puts
 * the "…" at the end of the last visible line every time, directly above the
 * "See more" control.
 *
 * Only the *collapsed* preview uses this. Expanding renders the original string,
 * so the author's paragraphs come back untouched.
 */
export const flattenForClamp = (value?: string | null): string =>
  (value ?? "").replace(/\s*\r?\n\s*/g, " ").trim();
